import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Label } from 'office-ui-fabric-react/lib/Label';
import useAPI from '../../hooks/useAPI';
import { searchByField } from '../../actions/search';
import styles from './GeographyTypeAhead.style';
import ActionTypeaheadPicker from '../TypeaheadPicker/ActionTypeaheadPicker';

const GeographyTypeAhead = ({
  field: { name },
  form: { errors = {}, setFieldValue = () => {}, setFieldTouched = () => {} },
  label,
  required,
  selectedValue,
  dataTestId,
  ...props
}) => {
  const setFormValue = value => {
    setFieldValue(name, value);
    setFieldTouched(name);
  };

  const errorMessage = get(errors, name);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [selectedGeo, setSelectedGeo] = useState({ key: selectedValue, text: selectedValue });
  const { data: suggestions, isLoading } = useAPI(
    () => searchByField('geographies', '', { start: 0, count: 650 }),
    [],
  );

  const onTypeAction = async newInput => {
    if (!newInput) return;

    const searchResults = suggestions
      .filter(geo => geo.ISOCountryCode.toLowerCase().includes(newInput.toLowerCase()))
      .map(geo => ({
        key: geo.ISOCountryCode,
        text: geo.ISOCountryCode,
      }))
      .slice(0, 5);

    setSearchSuggestions(searchResults);
  };

  const onGeographySelection = geo => {
    setSelectedGeo(geo);
    setFormValue(geo.key);
  };

  return (
    <Stack>
      {label && (
        <Stack horizontal verticalAlign="start">
          <Label data-testid="typeahead-label">{label}</Label>
          {required && <Text className={styles.asterisk}>*</Text>}
        </Stack>
      )}

      <ActionTypeaheadPicker
        options={searchSuggestions}
        selectedValue={selectedGeo}
        onChange={onGeographySelection}
        onTypeAction={onTypeAction}
        isLoading={isLoading}
        placeholder="Search"
        showDropdownChevron={false}
        aria-required={required}
        dataTestId={dataTestId}
        {...props}
      />

      {errorMessage && (
        <div role="alert">
          <p className={styles.errorMessage}>
            <span data-testid="typeahead-required" data-automation-id="error-message">
              {errorMessage}
            </span>
          </p>
        </div>
      )}
    </Stack>
  );
};

GeographyTypeAhead.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}),
    setFieldTouched: PropTypes.func,
    setFieldValue: () => {},
  }).isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  selectedValue: PropTypes.string,
  dataTestId: PropTypes.string,
};

GeographyTypeAhead.defaultProps = {
  required: false,
  label: null,
  selectedValue: null,
  dataTestId: 'geography-typeahead',
};

export default GeographyTypeAhead;
